// @flow
import React, { useCallback, useContext, useEffect } from 'react';
import PageHeader from '@atlaskit/page-header';
import styled from 'styled-components';
import { BreadcrumbsItem } from '@atlaskit/breadcrumbs';

import ErrorIcon from '@atlaskit/icon/glyph/error';
import Icon from '@atlaskit/icon';
import { formatDistance } from 'date-fns';
import Button from '@atlaskit/button';
import Spinner from '@atlaskit/spinner';
import RefreshIcon from '@atlaskit/icon/glyph/refresh';
import { Query, Subscription, compose, graphql } from 'react-apollo';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { useSetState } from 'react-use';
import Tick from '@atlaskit/icon/glyph/check-circle';
import { NetworkStatus } from 'apollo-client';
import { OCTO_GLOBAL_PERMISSIONS, ERROR_CODES } from '../utils/consts';
import SyncErrorsModal from '../components/virtual-components/SyncErrorsModal';
import OctoIcon from '../components/assets/octoIcon';
import { hasPermission } from '../utils/commons';
import { COMPONENTS_SYNC_IN_PROGRESS } from '../graphql/subscriptions';
import { SYNC_DATA, SYNC_ERRORS, IMPORT_COMPONENTS_AND_SYNCHRONIZE_PROCESS_QUERY } from '../graphql/queries';
import { SYNCHRONIZE, ACKNOWLEDGE_SYNC_ERRORS } from '../graphql/mutations';
import Loader from '../components/utils/Loader/Loader';
import OctoPermissionsContext from '../providers/OctoPermissionsContext';
import useFlagContext from '../hooks/useFlagContext';

const HeaderRow = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: baseline;
    align-content: center;
    h1 {
        margin-bottom: 0;
    }
    .title {
        flex-grow: 1;
    }
    .button {
        flex-grow: 0;
        flex-basis: 148px;
    }

    div.sync-data {
        transform: translateY(-10px);
        p {
            font-size: 12px;
            line-height: 6px;
            color: gray;
        }
    }
`;

const BreadcrumbsView = styled.div`
    * {
        cursor: default;

        &:hover {
            text-decoration: none !important;
            color: #6b778c !important;
        }
    }
    span.css-1h9rjj2 {
        margin-left: 7px;
    }
`;

const RED = '#DE350B';
const GREEN = '#00875A';

const initialState = {
    displaySyncErrorsModal: false,
    synchronizing: false,
    lastSyncDate: null,
    hasSyncErrors: false,
};

const VirtualComponentPageHeader = ({
    displaySynchronizedStatus,
    syncErrorsQueryData,
    acknowledgeMutation,
    synchronizeMutation,
    lockProcessesQuery,
    pageTitle,
    actions,
    synchronizeKey,
}) => {
    const permissions = useContext(OctoPermissionsContext);
    const { dismissFlag, pushFlag } = useFlagContext();
    const shouldDisplaySynchronizedStatus =
        displaySynchronizedStatus && hasPermission(OCTO_GLOBAL_PERMISSIONS.MANAGE, permissions);

    const [state, setState] = useSetState(initialState);
    const setDisplaySyncErrorsModal = useCallback(
        (display) => setState({ displaySyncErrorsModal: display }),
        [setState],
    );
    const setLastSyncDate = useCallback(
        ({ syncData }) => {
            if (syncData?.val?.date) setState({ lastSyncDate: new Date(syncData.val.date) });
        },
        [setState],
    );

    const acknowledgeSyncErrors = useCallback(() => {
        const ids = syncErrorsQueryData?.syncErrors?.map((se) => se.id);

        setState({ synchronizing: true });
        acknowledgeMutation({
            variables: { ids },
            refetchQueries: [{ query: SYNC_ERRORS }],
            awaitRefetchQueries: true,
        }).finally(() => setState({ synchronizing: false, displaySyncErrorsModal: false }));
    }, [acknowledgeMutation, setState, syncErrorsQueryData]);

    const onSynchronize = useCallback(() => {
        setState({ synchronizing: true });
        synchronizeMutation({
            refetchQueries: [{ query: SYNC_ERRORS }, { query: SYNC_DATA }],
            awaitRefetchQueries: true,
        })
            .then(({ data }) => {
                const synchronizedSuccessfully = !!data?.synchronize?.success;
                const notification = synchronizedSuccessfully
                    ? {
                          icon: <Tick primaryColor={GREEN} />,
                          title: 'Synchronization successful.',
                          key: 'success',
                      }
                    : {
                          icon: <ErrorIcon primaryColor={RED} />,
                          title: 'One or more errors occurred.',
                          key: 'error',
                      };
                pushFlag(notification);
                setState({ hasSyncErrors: !synchronizedSuccessfully });
            })
            .catch((err) => {
                let notification = {
                    icon: <ErrorIcon primaryColor={RED} />,
                    title: 'One or more errors occurred.',
                    key: 'error',
                };
                const syncInProgress = err?.message?.indexOf(ERROR_CODES.SYNC_IN_PROGRESS) >= 0;
                const importInProgress = err?.message?.indexOf(ERROR_CODES.IMPORT_IN_PROGRESS) >= 0;
                if (syncInProgress) {
                    notification = {
                        appearance: 'warning',
                        icon: <WarningIcon primaryColor={RED} />,
                        title: `Synchronization in progress...`,
                        description: `Another synchronization process is already in progress. Please wait until it's done and try again.`,
                        key: 'Warning',
                        autoDismiss: false,
                        actions: [
                            {
                                content: 'Dismiss',
                                onClick: dismissFlag,
                            },
                        ],
                    };
                } else if (importInProgress) {
                    notification = {
                        appearance: 'warning',
                        icon: <WarningIcon primaryColor={RED} />,
                        title: `Import components in progress...`,
                        description: `An import components process(which calls sync process) is already in progress. Please wait until it's done and try again.`,
                        key: 'Warning',
                        autoDismiss: false,
                        actions: [
                            {
                                content: 'Dismiss',
                                onClick: dismissFlag,
                            },
                        ],
                    };
                }
                setState({ hasSyncErrors: !syncInProgress && !importInProgress });
                pushFlag(notification);
            })
            .finally(() => setState({ synchronizing: false }));
    }, [pushFlag, setState, synchronizeMutation, dismissFlag]);

    useEffect(() => {
        if (synchronizeKey) {
            // if synchronizeKey changed, it means the parent component is requesting synchronize
            onSynchronize();
        }
    }, [synchronizeKey, onSynchronize]);

    return (
        <HeaderRow
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
            }}
        >
            <div className="title">
                <PageHeader
                    breadcrumbs={
                        <BreadcrumbsView>
                            <BreadcrumbsItem
                                iconBefore={<Icon glyph={OctoIcon} label="Octo icon" size="medium" />}
                                text="Octo"
                            />
                        </BreadcrumbsView>
                    }
                >
                    {pageTitle}
                </PageHeader>
            </div>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '4px',
                }}
            >
                {shouldDisplaySynchronizedStatus && (
                    <>
                        <div
                            style={{
                                fontSize: '12px',
                            }}
                        >
                            <div>Last synchronized</div>
                            <Query displayName="syncData" query={SYNC_DATA} onCompleted={setLastSyncDate}>
                                {({ loading, error, networkStatus }) => {
                                    if (loading || networkStatus === NetworkStatus.refetch) {
                                        return <Loader size="small" />;
                                    }

                                    if (error) {
                                        return `Error! ${error.message}`;
                                    }
                                    const { lastSyncDate } = state;
                                    return (
                                        <div>
                                            {lastSyncDate ? `${formatDistance(new Date(), lastSyncDate)} ago` : '-'}
                                        </div>
                                    );
                                }}
                            </Query>
                        </div>
                        <div>
                            <Subscription subscription={COMPONENTS_SYNC_IN_PROGRESS}>
                                {({ data }) => {
                                    if (state.hasSyncErrors) {
                                        // This button is displayed only after onSynchronize function was manually triggered and errors were returned
                                        return (
                                            <Button
                                                onClick={() => setDisplaySyncErrorsModal(true)}
                                                iconBefore={<ErrorIcon primaryColor={RED} />}
                                            />
                                        );
                                    }
                                    const syncOrImportIsActive =
                                        lockProcessesQuery.getImportComponentsProcess?.active ||
                                        lockProcessesQuery.getSynchronizeComponentsProcess?.active;

                                    const showLoadingIndicator =
                                        state.synchronizing ||
                                        (syncOrImportIsActive && !data) ||
                                        !!data?.componentsSyncInProgress;

                                    return (
                                        <Button
                                            // style={{
                                            //     margin: '0 0 0 0px',
                                            // }}
                                            id="sync"
                                            isDisabled={state.synchronizing}
                                            onClick={onSynchronize}
                                            iconBefore={showLoadingIndicator ? <Spinner /> : <RefreshIcon />}
                                        />
                                    );
                                }}
                            </Subscription>
                            {state.displaySyncErrorsModal && (
                                <SyncErrorsModal
                                    errors={syncErrorsQueryData?.syncErrors}
                                    setIsOpen={setDisplaySyncErrorsModal}
                                    loading={state.synchronizing}
                                    confirm={acknowledgeSyncErrors}
                                />
                            )}
                        </div>
                    </>
                )}
                {(actions || [])
                    .filter((action) => action.visible)
                    .map((action) => (
                        <div key={action.key}>{action.render()}</div>
                    ))}
            </div>
        </HeaderRow>
    );
};

export default compose(
    graphql(IMPORT_COMPONENTS_AND_SYNCHRONIZE_PROCESS_QUERY, { name: 'lockProcessesQuery' }),
    graphql(ACKNOWLEDGE_SYNC_ERRORS, { name: 'acknowledgeMutation' }),
    graphql(SYNCHRONIZE, { name: 'synchronizeMutation' }),
)(VirtualComponentPageHeader);
